import { useEffect, useState } from "react";
import { PostType } from "@/core/types/contentTypes";
import { DataRequestStateEnum, DataRequestType } from "@/core/types/dataRequestsTypes";
import useDataRequestState from "@/core/dataProviders/hooks/useDataRequestState";
import { useAppSelector } from "@/core/store/reduxHooks";
import { PostsByAuthorSelector } from "@/core/store/posts/PostsSlice";
import useDataRequestOrder from "@/core/dataProviders/hooks/useDataRequestOrder";
import usePostsFiller from "@/core/dataProviders/hooks/contentFillers/usePostsFiller";
import useLangSelector from "@/core/dataProviders/hooks/useLangSelector";
import isNewOrderNeeded from "@/core/dataProviders/utils/isNewOrderNeeded";

/**
 * data provider for front-end pages based on available options for given data
 * - checks if order exists / creates orders / returns order status
 * - gathers and returns requested data
 * */

const usePostsByAuthorSlugProvider = (
  sign: {
    slug: string;
    pageNumber: number;
    perPage?: number;
    excludeIds?: number[];
  } | null
): [PostType[], DataRequestStateEnum] => {
  const orderData = useDataRequestOrder();
  const postFiller = usePostsFiller();
  const postsByAuthor = useLangSelector(useAppSelector(PostsByAuthorSelector));

  const slug = sign ? sign.slug : null;
  const perPage = sign ? sign.perPage : null;
  const pageNumber = sign ? sign.pageNumber : null;
  const excludeIds = sign ? sign.excludeIds : null;

  const [currentReqState, , setReqSign] = useDataRequestState({
    type: DataRequestType.POSTS_BY_AUTHOR_SLUG,
    sign: sign,
  });
  const [currentPosts, setCurrentPosts] = useState<PostType[]>(() =>
    sign && postsByAuthor && postsByAuthor[JSON.stringify(sign)] ? postFiller(postsByAuthor[JSON.stringify(sign)]) : []
  );

  useEffect(() => {
    setCurrentPosts(
      sign && postsByAuthor && postsByAuthor[JSON.stringify(sign)]
        ? postFiller(postsByAuthor[JSON.stringify(sign)])
        : []
    );
    setReqSign(sign);
  }, [slug, perPage, pageNumber, excludeIds]);

  useEffect(() => {
    setCurrentPosts(
      sign && postsByAuthor && postsByAuthor[JSON.stringify(sign)]
        ? postFiller(postsByAuthor[JSON.stringify(sign)])
        : []
    );
  }, [postsByAuthor]);

  useEffect(() => {
    if (!sign || isNewOrderNeeded(!!currentPosts.length, currentReqState)) {
      return;
    }

    orderData({
      type: DataRequestType.POSTS_BY_AUTHOR_SLUG,
      sign: sign,
    });
  }, [currentReqState, orderData]);

  return [currentPosts, currentReqState];
};

export default usePostsByAuthorSlugProvider;
